// Left sidebar (240px wide). Reads from the router's registered pages and
// renders one row per page with icon + label.
//
// Behaviour notes:
//   - pages with `hidden: true` are skipped — they're routable but not in nav
//   - the active page is highlighted with a primary-coloured left border + bg
//   - clicking a link calls `navigate(name)` and notifies callers (used by
//     the shell to close the mobile drawer)

import { listPages, navigate, onNavigate, getCurrent } from "./router";

export interface NavOptions {
  /** Called whenever a sidebar link is clicked (so the shell can close
   * the mobile drawer). */
  onAfterNavigate?: () => void;
}

export function createSidebar(opts: NavOptions = {}): HTMLElement {
  const aside = document.createElement("aside");
  aside.className = "adm-sidebar";
  aside.setAttribute("aria-label", "Primary navigation");

  const list = document.createElement("ul");
  list.className = "adm-sidebar__list";
  aside.appendChild(list);

  const buttons = new Map<string, HTMLButtonElement>();

  const setActive = (name: string | null): void => {
    for (const [pageName, btn] of buttons) {
      const isActive = pageName === name;
      btn.classList.toggle("is-active", isActive);
      if (isActive) {
        btn.setAttribute("aria-current", "page");
      } else {
        btn.removeAttribute("aria-current");
      }
    }
  };

  const render = (): void => {
    list.replaceChildren();
    buttons.clear();
    for (const page of listPages()) {
      if (page.hidden) continue;

      const li = document.createElement("li");
      li.className = "adm-sidebar__item";

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "adm-sidebar__link";
      btn.dataset["page"] = page.name;

      const icon = document.createElement("span");
      icon.className = "adm-sidebar__icon";
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = page.icon ?? "·";

      const label = document.createElement("span");
      label.className = "adm-sidebar__label";
      label.textContent = page.label;

      btn.append(icon, label);
      btn.addEventListener("click", () => {
        navigate(page.name);
        opts.onAfterNavigate?.();
      });

      li.appendChild(btn);
      list.appendChild(li);
      buttons.set(page.name, btn);
    }

    setActive(getCurrent());
  };

  render();
  onNavigate((name) => setActive(name));

  return aside;
}
